/**
 * Hook useWebSocket - Phase 1: Dashboard Temps Réel
 * 
 * Gère la connexion WebSocket (Socket.IO) au backend et l'écoute des événements temps réel.
 * 
 * Événements écoutés:
 * - conversation_update: Nouveau message, changement statut
 * - takeover_event: Takeover/Release manuel ou automatique
 * - notification: Notifications système
 * 
 * Usage:
 * ```tsx
 * const { isConnected, subscribe } = useWebSocket()
 * 
 * useEffect(() => {
 *   const unsubscribe = subscribe('conversation_update', (data) => {
 *     console.log('Conversation updated:', data)
 *     // Rafraîchir la liste
 *   })
 *   return unsubscribe
 * }, [subscribe])
 * ```
 */
import { useEffect, useRef, useState, useCallback } from 'react'
import { io, Socket } from 'socket.io-client'
import { getApiBase, getToken } from '../lib/utils'

interface WebSocketEvent {
  conversation_update: {
    conversation_id: number
    action: 'message_added' | 'status_changed' | 'takeover' | 'release'
    data: any
    timestamp: string
  }
  takeover_event: {
    conversation_id: number
    event_type: 'takeover' | 'release' | 'auto_takeover' | 'auto_release'
    trigger: string
    metadata: any
    timestamp: string
  }
  notification: {
    type: 'info' | 'warning' | 'error' | 'success'
    title: string
    message: string
    metadata: any
  }
}

type EventName = keyof WebSocketEvent
type EventHandler<T extends EventName> = (data: WebSocketEvent[T]) => void

export function useWebSocket() {
  const [isConnected, setIsConnected] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const socketRef = useRef<Socket | null>(null)
  const handlersRef = useRef<Map<string, Set<Function>>>(new Map())
  
  // Initialiser la connexion WebSocket
  useEffect(() => {
    const token = getToken()
    
    if (!token) {
      setError('No authentication token')
      return
    }
    
    // Extraire user_id depuis le token (simple parsing JWT sans vérification)
    let userId: number | null = null
    try {
      const payload = JSON.parse(atob(token.split('.')[1]))
      userId = payload.user_id || payload.sub
    } catch (e) {
      console.error('Failed to parse token:', e)
    }
    
    if (!userId) {
      setError('No user_id in token')
      return
    }
    
    // Utiliser l'URL complète du domaine (pas /api)
    // Socket.IO doit se connecter à la racine du domaine car nginx proxy /socket.io/ vers le backend
    const wsUrl = window.location.origin
    
    console.log('🔌 Connecting to WebSocket:', wsUrl)
    
    // Créer connexion Socket.IO
    const socket = io(wsUrl, {
      path: '/socket.io',
      transports: ['websocket', 'polling'], // Try WebSocket first, fallback to polling
      auth: {
        token: token
      },
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      reconnectionAttempts: Infinity, // Toujours reconnecter
      timeout: 60000, // 60s timeout au lieu du défaut 20s
      forceNew: false, // Réutiliser connexion existante
      upgrade: true, // Autoriser upgrade websocket → polling
      rememberUpgrade: true // Se souvenir du transport qui fonctionne
    })
    
    socketRef.current = socket
    
    // Événements de connexion
    socket.on('connect', () => {
      console.log('✅ WebSocket connected:', socket.id)
      setIsConnected(true)
      setError(null)
      
      // S'abonner aux événements de l'utilisateur
      socket.emit('subscribe_user', { user_id: userId })
    })
    
    socket.on('disconnect', (reason) => {
      console.warn('❌ WebSocket disconnected:', reason)
      console.warn('  - Socket ID:', socket.id)
      console.warn('  - Reason details:', reason)
      setIsConnected(false)
      
      // Log des raisons courantes
      if (reason === 'io server disconnect') {
        console.error('🔴 Server forcefully disconnected the socket')
      } else if (reason === 'io client disconnect') {
        console.warn('🟡 Client disconnected (manual)')
      } else if (reason === 'ping timeout') {
        console.error('🔴 Ping timeout - connection lost')
      } else if (reason === 'transport close') {
        console.warn('🟡 Transport closed')
      } else if (reason === 'transport error') {
        console.error('🔴 Transport error')
      }
    })
    
    socket.on('connect_error', (err) => {
      console.error('❌ WebSocket connection error:', err.message)
      setError(err.message)
      setIsConnected(false)
    })
    
    // Événements applicatifs
    socket.on('conversation_update', (data: WebSocketEvent['conversation_update']) => {
      console.log('📨 conversation_update:', data)
      notifyHandlers('conversation_update', data)
    })
    
    socket.on('takeover_event', (data: WebSocketEvent['takeover_event']) => {
      console.log('👤 takeover_event:', data)
      notifyHandlers('takeover_event', data)
    })
    
    socket.on('notification', (data: WebSocketEvent['notification']) => {
      console.log('🔔 notification:', data)
      notifyHandlers('notification', data)
    })
    
    // Cleanup
    return () => {
      console.log('🔌 Disconnecting WebSocket')
      socket.disconnect()
    }
  }, [])
  
  // Notifier tous les handlers d'un événement
  const notifyHandlers = useCallback((eventName: EventName, data: any) => {
    const handlers = handlersRef.current.get(eventName)
    if (handlers) {
      handlers.forEach(handler => handler(data))
    }
  }, [])
  
  // S'abonner à un événement
  const subscribe = useCallback(<T extends EventName>(
    eventName: T,
    handler: EventHandler<T>
  ): () => void => {
    if (!handlersRef.current.has(eventName)) {
      handlersRef.current.set(eventName, new Set())
    }
    
    handlersRef.current.get(eventName)!.add(handler)
    
    // Retourner fonction de désabonnement
    return () => {
      const handlers = handlersRef.current.get(eventName)
      if (handlers) {
        handlers.delete(handler)
      }
    }
  }, [])
  
  // Émettre un événement (pour usage futur)
  const emit = useCallback((eventName: string, data: any) => {
    if (socketRef.current && socketRef.current.connected) {
      socketRef.current.emit(eventName, data)
    } else {
      console.warn('⚠️ Cannot emit: WebSocket not connected')
    }
  }, [])
  
  return {
    isConnected,
    error,
    subscribe,
    emit
  }
}
